import styled, { css } from "styled-components";
import { CheckboxProps } from "./Checkbox.types";

const CheckboxContainer = styled.div`
    & > :not(label:last-child) {
        margin-right: 12px;
    }
`;

const CheckboxLabel = styled.label`
    position: relative;
    display: inline-flex;
    user-select: none;

    &:focus-visible {
        outline: none;
    }
`;

const CheckboxSpan = styled.span<CheckboxProps>`
    display: inline-flex;
    font-size: 14px;
    color: ${({ theme, error }) => `${error ? theme.colors.error.strong : "#bfbfbf"}`};
    cursor: pointer;

    ${({ variant, error }) => {
        switch (variant) {
            case "noBorder":
                return css`
                    align-items: flex-start;

                    &::before {
                        margin-top: 4px;
                    }

                    &:hover {
                        &::before {
                            background-color: ${({ theme }) => theme.colors.lightGray100};
                        }
                    }
                `;
            case "withBorder":
                return css`
                    line-height: 0;
                    align-items: center;
                    min-height: 30px;
                    border: 1px solid;
                    border-color: ${({ theme }) => `${error ? theme.colors.error.strong : theme.colors["white.50"]}`};
                    border-radius: 2px;
                    padding: 0 12px;

                    &:hover {
                        background-color: ${({ theme }) => theme.colors.blue.light};
                        border-color: ${({ theme }) => theme.colors.blue.light};
                        color: ${({ theme }) => theme.colors["black.100"]};

                        &::before {
                            border-color: ${({ theme }) => theme.colors["black.100"]};
                        }
                    }

                    &:active {
                        background-color: ${({ theme }) => theme.colors.blue.base};
                        border-color: ${({ theme }) => theme.colors.blue.base};
                        color: ${({ theme }) => theme.colors["black.100"]};

                        &::before {
                            background-color: ${({ theme }) => theme.colors.blue.base};
                        }
                    }
                `;
            default:
                return css``;
        }
    }};

    &::before {
        content: "";
        display: inline-flex;
        min-width: 14px;
        min-height: 14px;
        border: 1px solid;
        border-color: ${({ theme, error }) => `${error ? theme.colors.error.strong : "#bfbfbf"}`};
        border-radius: 2px;
        margin-right: 10px;
    }

    &::after {
        content: "";
        position: absolute;
    }

    &:focus-visible {
        outline: none;
    }
`;

const CheckboxInput = styled.input<Partial<CheckboxProps>>`
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;

    ${({ variant, error }) => {
        switch (variant) {
            case "noBorder":
                return css`
                    &:checked + ${CheckboxSpan} {
                        color: ${({ theme }) => `${error ? theme.colors.error.strong : theme.colors["white.100"]}`};

                        &::before {
                            color: ${({ theme }) => theme.colors["white.100"]};
                        }

                        &::after {
                            display: inline-flex;
                            top: 7px;
                            left: 5.5px;
                            width: 3px;
                            height: 7px;
                            color: ${({ theme }) => theme.colors["white.100"]};
                            border: solid ${({ theme }) => theme.colors["black.100"]};
                            border-color: ${({ theme }) => theme.colors["white.75"]};
                            border-width: 0 1px 1px 0;
                            transform: rotate(45deg);
                        }
                    }

                    &:focus-visible {
                        outline: none;
                    }

                    &:disabled + ${CheckboxSpan} {
                        pointer-events: none;
                        color: ${({ theme }) => theme.colors["white.25"]};

                        &::before,
                        &::after {
                            border-color: ${({ theme }) => theme.colors["white.25"]};
                        }
                    }

                    &:focus-visible + ${CheckboxSpan} {
                        &::before {
                            outline: 2px solid ${({ theme }) => theme.colors.blue.medium};
                            border: none;
                        }
                    }
                `;
            case "withBorder":
                return css`
                    &:checked + ${CheckboxSpan} {
                        background-color: ${({ theme }) => theme.colors.blue.base};
                        border-color: ${({ theme }) => theme.colors.blue.base};
                        color: ${({ theme }) => theme.colors["black.100"]};

                        &::before {
                            border-color: ${({ theme }) => theme.colors["black.100"]};
                        }

                        &::after {
                            display: inline-flex;
                            top: 11px;
                            left: 18.5px;
                            width: 3px;
                            height: 7px;
                            border: solid ${({ theme }) => theme.colors["black.100"]};
                            border-width: 0 1px 1px 0;
                            transform: rotate(45deg);
                        }
                    }

                    &:disabled {
                        & + ${CheckboxSpan} {
                            pointer-events: none;
                        }

                        &:not(:checked) + ${CheckboxSpan} {
                            pointer-events: none;
                            color: ${({ theme }) => theme.colors["white.25"]};
                            border-color: ${({ theme }) => theme.colors["white.25"]};
                            background-color: transparent;

                            &::before,
                            &::after {
                                border-color: ${({ theme }) => theme.colors["white.25"]};
                            }
                        }
                    }

                    &:focus-visible + ${CheckboxSpan} {
                        outline: 2px solid ${({ theme }) => theme.colors.blue.medium};
                        border-color: ${({ theme }) => theme.colors.blue.medium};
                    }
                `;
            default:
                return css``;
        }
    }}
`;

export { CheckboxContainer, CheckboxLabel, CheckboxInput, CheckboxSpan };
